import { Alert, Button, Result, Space, Spin } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { SessionResponseSchema, WebOidcConfigSchema, type PageKey, type SessionResponse } from '@youkuai/contracts';
import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts';
import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { buildSigninRedirectArgs, type LoginMethod } from './auth-login.ts';
import { installInsecureHttpCryptoCompatibility } from './insecure-http-crypto.ts';
import { onSessionExpired } from './project-api.ts';

type AuthState =
  | { kind: 'loading' }
  | { kind: 'anonymous'; expired?: boolean }
  | { kind: 'ready'; token: string; session: SessionResponse }
  | { kind: 'error'; message: string };

interface AuthContextValue {
  state: AuthState;
  signIn(method: LoginMethod): Promise<void>;
  signOut(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

async function makeManager(): Promise<UserManager> {
  const response = await fetch('/api/auth/oidc-config', { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`身份配置不可用（${response.status}）`);
  const config = WebOidcConfigSchema.parse(await response.json());
  if (config.allowInsecureHttpAuth && !window.isSecureContext) installInsecureHttpCryptoCompatibility();
  return new UserManager({
    authority: config.issuer,
    client_id: config.clientId,
    redirect_uri: `${window.location.origin}/auth/callback`,
    silent_redirect_uri: `${window.location.origin}/auth/silent-callback`,
    post_logout_redirect_uri: `${window.location.origin}/`,
    response_type: 'code',
    scope: 'openid profile',
    automaticSilentRenew: true,
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  });
}

// React StrictMode replays effects in development. The OIDC callback consumes its
// one-time PKCE state, so both effect runs must await the same callback operation.
let managerPromise: Promise<UserManager> | null = null;
let callbackPromise: Promise<User> | null = null;
let silentCallbackPromise: Promise<void> | null = null;

function getManager(): Promise<UserManager> {
  managerPromise ??= makeManager().catch((error: unknown) => {
    managerPromise = null;
    throw error;
  });
  return managerPromise;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<AuthState>({ kind: 'loading' });
  const [manager, setManager] = useState<UserManager | null>(null);
  const removingExpiredUser = useRef<Promise<void>>(Promise.resolve());
  const refreshingSession = useRef<Promise<User | null> | null>(null);
  const currentToken = state.kind === 'ready' ? state.token : null;
  const previousToken = useRef<string | null>(null);

  useEffect(() => {
    if (currentToken && previousToken.current && currentToken !== previousToken.current) {
      void queryClient.invalidateQueries();
    }
    previousToken.current = currentToken;
  }, [currentToken, queryClient]);

  useEffect(() => {
    if (!manager) return;
    const syncRenewedToken = (user: User) => {
      if (!user.access_token) return;
      setState((current) => current.kind === 'ready' ? { ...current, token: user.access_token } : current);
    };
    manager.events.addUserLoaded(syncRenewedToken);
    return () => manager.events.removeUserLoaded(syncRenewedToken);
  }, [manager]);

  useEffect(() => {
    if (state.kind !== 'ready') return;
    const currentToken = state.token;
    return onSessionExpired((expiredToken) => {
      if (expiredToken !== currentToken) return;
      void (async () => {
        const client = manager ?? await getManager();
        const refresh = refreshingSession.current ??= client.signinSilent().catch(() => null);
        const renewed = await refresh;
        if (refreshingSession.current === refresh) refreshingSession.current = null;
        if (renewed?.access_token && !renewed.expired) {
          setState((current) => current.kind === 'ready' && current.token === expiredToken
            ? { ...current, token: renewed.access_token } : current);
          return;
        }
        queryClient.clear();
        setState({ kind: 'anonymous', expired: true });
        removingExpiredUser.current = client.removeUser().catch(() => undefined);
      })();
    });
  }, [manager, queryClient, state]);

  useEffect(() => {
    let active = true;
    async function initialize() {
      try {
        const client = await getManager();
        if (window.location.pathname === '/auth/silent-callback') {
          silentCallbackPromise ??= client.signinSilentCallback();
          await silentCallbackPromise;
          return;
        }
        let user;
        if (window.location.pathname === '/auth/callback') {
          callbackPromise ??= client.signinRedirectCallback();
          user = await callbackPromise;
          if (!active) return;
          const returnTo = typeof user.state === 'object' && user.state !== null &&
            'returnTo' in user.state && typeof user.state.returnTo === 'string' && user.state.returnTo.startsWith('/') &&
            !user.state.returnTo.startsWith('//') ? user.state.returnTo : '/projects';
          window.location.replace(returnTo);
          return;
        } else {
          user = await client.getUser();
        }
        if (!active) return;
        setManager(client);
        if (!user || user.expired || !user.access_token) {
          setState({ kind: 'anonymous' });
          return;
        }
        const response = await fetch('/api/auth/session', {
          headers: { Authorization: `Bearer ${user.access_token}`, Accept: 'application/json' },
        });
        if (response.status === 401) {
          queryClient.clear();
          await client.removeUser();
          setState({ kind: 'anonymous', expired: true });
          return;
        }
        if (response.status === 403) {
          setState({ kind: 'error', message: '身份已登录，但没有有效的系统账号或权限。请联系管理员。' });
          return;
        }
        if (!response.ok) throw new Error(`系统会话核验失败（${response.status}）`);
        const session = SessionResponseSchema.parse(await response.json());
        if (session.client !== 'web') throw new Error('当前 Token 不是 Web 客户端签发');
        setState({ kind: 'ready', token: user.access_token, session });
      } catch (error) {
        if (active) setState({ kind: 'error', message: error instanceof Error ? error.message : '身份核验失败' });
      }
    }
    void initialize();
    return () => { active = false; };
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(() => ({
    state,
    async signIn(method) {
      await removingExpiredUser.current;
      const client = manager ?? await getManager();
      const returnTo = window.location.pathname + window.location.search + window.location.hash;
      await client.signinRedirect(buildSigninRedirectArgs(method, returnTo));
    },
    async signOut() {
      queryClient.clear();
      setState({ kind: 'anonymous' });
      if (manager) await manager.signoutRedirect();
      else window.location.assign('/');
    },
  }), [manager, queryClient, state]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('AuthProvider is missing');
  return value;
}

function LoginActions({ auth, expired = false }: { auth: AuthContextValue; expired?: boolean }) {
  return (
    <Space wrap>
      <Button type="primary" onClick={() => void auth.signIn('feishu')}>
        {expired ? '使用飞书重新登录' : '飞书员工登录'}
      </Button>
      <Button onClick={() => void auth.signIn('account')}>外部协作者登录</Button>
    </Space>
  );
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const auth = useAuth();
  if (auth.state.kind === 'loading') return <div className="auth-gate"><Spin size="large" /><p>正在验证身份与项目权限…</p></div>;
  if (auth.state.kind === 'error') return (
    <main className="page-shell auth-gate"><Alert type="error" showIcon title="无法进入工作区" description={auth.state.message} />
      <LoginActions auth={auth} /></main>
  );
  if (auth.state.kind === 'anonymous') return (
    <main className="page-shell auth-gate"><Result status="403"
      title={auth.state.expired ? '登录已过期' : '请先登录'}
      subTitle={auth.state.expired ? '已清除本机业务缓存；重新登录后返回当前页面核对操作结果。' : '仅内部员工和长期合作执行人员可访问自己的项目。'}
      extra={<LoginActions auth={auth} expired={auth.state.expired === true} />} /></main>
  );
  return children;
}

export function RequirePage({ page, children }: { page: PageKey; children: ReactNode }) {
  const auth = useAuth();
  if (auth.state.kind !== 'ready') return null;
  if (auth.state.session.pageAccess.includes(page)) return children;
  if (auth.state.session.pageAccess.length === 0) return (
    <main className="page-shell auth-gate"><Result status="info" title="账号已激活，暂未分配项目权限"
      subTitle="飞书身份和应用准入已通过。系统管理员需要在项目系统中分配角色或项目成员关系后，才会显示对应页面和数据。" />
    </main>
  );
  return <main className="page-shell auth-gate"><Result status="403" title="无权访问此页面"
    subTitle={`当前角色未获授页面权限 ${page}。导航隐藏与直接地址访问使用同一权限规则。`}
    extra={<Button type="primary" onClick={() => window.location.assign('/workspace')}>返回工作台</Button>} /></main>;
}
